/**
 * The public pricing response, fetched once and shared.
 *
 * Three parts of the site read it on a first visit: the homepage and products
 * grid (prices and sales switches), the product page (prices), and the click
 * tracker (only to read settings.heatmapEnabled). Each used to fetch it for
 * itself -- the tracker from the function URL, which no CDN sits in front of
 * -- so a homepage load asked for the same data twice.
 *
 * Fetched through Firebase Hosting rather than straight at the function:
 * anonymous replies are marked s-maxage=300, which only a shared cache
 * honours. This never sends an Authorization header, so it always gets the
 * generic, cacheable body. (usePricing() does send one when a user is signed
 * in, since that reply can carry a referral discount, and stays separate.)
 */
const PRICING_URL = "https://veyra.services/v2GetPricing";

/* Reused for a minute, including while it is still in flight, so callers
   that start together share one request. */
const REUSE_MS = 60_000;
let cached: { at: number; request: Promise<PricingJson | null> } | null = null;

// The response is wide and only partly typed by its readers.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type PricingJson = { success?: boolean; games?: Record<string, any>; settings?: Record<string, any> };

export function getPricingJson(): Promise<PricingJson | null> {
  if (cached && Date.now() - cached.at < REUSE_MS) return cached.request;
  const request = fetch(PRICING_URL)
    .then((r) => (r.ok ? (r.json() as Promise<PricingJson>) : null))
    .then((data) => (data?.success ? data : null))
    .catch(() => null);
  cached = { at: Date.now(), request };
  /* A failure should not be remembered for a minute. */
  request.then((data) => {
    if (!data && cached?.request === request) cached = null;
  });
  return request;
}
